/**
 * usePlayerRoster — bench/active roster + per-player time tracking.
 *
 * Extracted from EstadisticasNew.js:
 *  - Initial player load via /api/cantera/:groupeId
 *  - playerTimeTracking state shape (realTimeSegments / videoTimeSegments /
 *    currentlyOnFloor / realTimeIn / videoTimeIn / periodIn)
 *  - handlePlayerCheckIn / handlePlayerCheckOut
 *  - The "force checkout all active players before save" logic from
 *    handleSaveGameAndActions, exposed as buildFinalTrackingForSave so the
 *    save flow can pre-build the snapshot without waiting for setState.
 *
 * The drag-drop swap callback is left to the screen layer (it depends on
 * react-dnd handlers in the UI), but this hook owns the underlying
 * activePlayers / players state slices the screen mutates.
 */

import { useState, useCallback, useEffect } from 'react';
import api from '../../../api/axiosConfig';
import { getPlayerAvatarUrl } from '../../utils/playerUtils';

export function usePlayerRoster({ groupeId, initialTeamName = '' }) {
  const [players, setPlayers] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [teamName, setTeamName] = useState(initialTeamName);
  const [playerTimeTracking, setPlayerTimeTracking] = useState({});

  // Initial load
  useEffect(() => {
    if (!groupeId) return;
    api.get(`/api/cantera/${groupeId}`)
      .then((response) => {
        if (response.data && response.data.players) {
          const fetchedPlayers = response.data.players.map((p) => ({
            ...p,
            avatarUrl: getPlayerAvatarUrl(p.id),
          }));
          setPlayers(fetchedPlayers);
        }
        if (response.data && response.data.group && response.data.group.libelle) {
          setTeamName(response.data.group.libelle);
        }
      })
      .catch((error) => {
        console.error('Error al obtener los jugadores:', error);
      });
  }, [groupeId]);

  // Mark a player as currently on the floor.
  // Caller passes the snapshot of clocks at the moment of the swap.
  const checkIn = useCallback((playerId, { periodSeconds, videoCurrentTime, currentPeriod }) => {
    setPlayerTimeTracking((prev) => {
      const data = prev[playerId] || {
        realTimeSegments: [],
        videoTimeSegments: [],
        currentlyOnFloor: false,
        realTimeIn: null,
        videoTimeIn: null,
        periodIn: null,
      };

      return {
        ...prev,
        [playerId]: {
          ...data,
          currentlyOnFloor: true,
          realTimeIn: periodSeconds,
          videoTimeIn: videoCurrentTime,
          periodIn: currentPeriod,
        },
      };
    });
  }, []);

  // Close the open segment for a player and push it onto realTime/videoTime arrays.
  const checkOut = useCallback((playerId, { periodSeconds, videoCurrentTime, currentPeriod }) => {
    setPlayerTimeTracking((prev) => {
      const pData = prev[playerId];
      if (!pData || !pData.currentlyOnFloor) return prev;

      const newRealSegment = {
        in: pData.realTimeIn,
        out: periodSeconds,
        periodIn: pData.periodIn,
        periodOut: currentPeriod,
      };

      const newVideoSegment = {
        in: pData.videoTimeIn,
        out: videoCurrentTime,
        periodIn: pData.periodIn,
        periodOut: currentPeriod,
      };

      return {
        ...prev,
        [playerId]: {
          ...pData,
          currentlyOnFloor: false,
          realTimeSegments: [...pData.realTimeSegments, newRealSegment],
          videoTimeSegments: [...pData.videoTimeSegments, newVideoSegment],
          realTimeIn: null,
          videoTimeIn: null,
          periodIn: null,
        },
      };
    });
  }, []);

  /**
   * Build a synchronous snapshot of playerTimeTracking with all currently
   * on-the-floor players force-checked-out. Mirrors the logic from
   * handleSaveGameAndActions which had to manually do this because setState
   * is async and the save payload needed the closed segments.
   */
  const buildFinalTrackingForSave = useCallback(({ periodSeconds, videoCurrentTime, currentPeriod }) => {
    const finalPlayerTimeTracking = { ...playerTimeTracking };

    activePlayers.forEach((p) => {
      const pData = finalPlayerTimeTracking[p.id];
      if (pData && pData.currentlyOnFloor) {
        const newRealSegment = {
          in: pData.realTimeIn,
          out: periodSeconds,
          periodIn: pData.periodIn,
          periodOut: currentPeriod,
        };
        const newVideoSegment = {
          in: pData.videoTimeIn,
          out: videoCurrentTime,
          periodIn: pData.periodIn,
          periodOut: currentPeriod,
        };

        finalPlayerTimeTracking[p.id] = {
          ...pData,
          currentlyOnFloor: false,
          realTimeSegments: [...pData.realTimeSegments, newRealSegment],
          videoTimeSegments: [...pData.videoTimeSegments, newVideoSegment],
          realTimeIn: null,
          videoTimeIn: null,
          periodIn: null,
        };
      }
    });

    return finalPlayerTimeTracking;
  }, [playerTimeTracking, activePlayers]);

  /**
   * Convenience: swap a player from bench to floor (or vice versa).
   * The drag-drop UI will provide its own swap helpers, but for click-to-toggle
   * flows this saves a few lines.
   */
  const moveBenchToFloor = useCallback((player) => {
    setPlayers((prev) => prev.filter((p) => p.id !== player.id));
    setActivePlayers((prev) => [...prev, player]);
  }, []);

  const moveFloorToBench = useCallback((player) => {
    setActivePlayers((prev) => prev.filter((p) => p.id !== player.id));
    setPlayers((prev) => [...prev, player]);
  }, []);

  const reset = useCallback(() => {
    setActivePlayers([]);
    setPlayerTimeTracking({});
  }, []);

  return {
    // state
    players,
    setPlayers,
    activePlayers,
    setActivePlayers,
    teamName,
    setTeamName,
    playerTimeTracking,
    setPlayerTimeTracking,

    // operations
    checkIn,
    checkOut,
    moveBenchToFloor,
    moveFloorToBench,
    buildFinalTrackingForSave,
    reset,
  };
}
